import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('register'); // 'register' or 'otp'
  const [tempId, setTempId] = useState(null);
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student',
    phone: '',
    referralCode: searchParams.get('ref') || '',
    instructorProfile: {
      qualification: '',
      experience: '',
      specialization: [],
      bio: '',
      linkedIn: '',
      portfolio: ''
    }
  });
  const [showInstructorFields, setShowInstructorFields] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [instructorRegistrationEnabled, setInstructorRegistrationEnabled] = useState(true);
  const [referralValid, setReferralValid] = useState(null);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [otpError, setOtpError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const { login } = useAuth(); // We'll use login to set auth state after successful registration
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
    if (formData.referralCode) {
      validateReferralCode(formData.referralCode);
    }
  }, [formData.referralCode]);

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setReferralValid(null);
      setReferrerInfo(null);
      return;
    }

    try {
      const response = await axios.post('/api/referrals/use-code', { referralCode: code });
      setReferralValid(true);
      setReferrerInfo(response.data.referrer);
    } catch (error) {
      setReferralValid(false);
      setReferrerInfo(null);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/admin/settings');
      setInstructorRegistrationEnabled(response.data.instructorRegistrationEnabled);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'role') {
      setShowInstructorFields(value === 'instructor');
      setFormData({
        ...formData,
        [name]: value
      });
    } else if (name.startsWith('instructorProfile.')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        instructorProfile: {
          ...formData.instructorProfile,
          [field]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (showInstructorFields && !formData.instructorProfile.qualification) {
      newErrors.qualification = 'Qualification is required for instructors';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await axios.post('/api/auth/register', registerData);

      if (response.data.requiresOTP) {
        setTempId(response.data.tempId);
        setStep('otp');
        setResendTimer(120); // 120 seconds (2 minutes) cooldown for resend
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp.trim()) {
      setOtpError('Please enter the OTP');
      return;
    }

    if (otp.length !== 6) {
      setOtpError('OTP must be 6 digits');
      return;
    }

    setIsLoading(true);
    setOtpError('');

    try {
      const response = await axios.post('/api/auth/verify-otp', {
        tempId,
        otp
      });

      // Registration successful
      if (response.data.token) {
        toast.success('Registration successful! Please login with your credentials.');
        // Redirect to login page
        navigate('/login');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'OTP verification failed';
      setOtpError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendDisabled) return;

    setResendDisabled(true);
    setResendTimer(120); // 120 seconds (2 minutes) cooldown for resend

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await axios.post('/api/auth/register', registerData);

      if (response.data.requiresOTP) {
        setTempId(response.data.tempId);
        setOtpError('');
      }
    } catch (error) {
      setOtpError('Failed to resend OTP. Please try again.');
      setResendDisabled(false);
      setResendTimer(0);
    }
  };

  const handleBackToRegister = () => {
    setStep('register');
    setTempId(null);
    setOtp('');
    setOtpError('');
    setErrors({});
  };

  if (step === 'otp') {
    return (
      <div className="bg-white py-8 px-6 rounded-lg">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            Verify Your Phone Number
          </h2>
          <p className="mt-2 text-sm text-gray-600 text-center">
            We've sent a 6-digit OTP to your WhatsApp number {formData.phone}
          </p>
        </div>

        <form onSubmit={handleOtpSubmit} className="space-y-4">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
              Enter OTP
            </label>
            <input
              id="otp"
              name="otp"
              type="text"
              maxLength="6"
              className={`input mt-1 text-center text-2xl tracking-widest ${otpError ? 'border-red-300' : ''}`}
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setOtp(value);
                if (otpError) setOtpError('');
              }}
              placeholder="000000"
            />
            {otpError && <p className="mt-1 text-sm text-red-600">{otpError}</p>}
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 btn btn-primary py-3 text-sm font-medium disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : 'Verify & Complete Registration'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleBackToRegister}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Back to edit details
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Didn't receive OTP?{' '}
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendDisabled}
                className={`font-medium ${resendDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:text-primary-500'}`}
              >
                {resendDisabled ? `Resend in ${resendTimer}s` : 'Resend OTP'}
              </button>
            </p>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white py-8 px-6 rounded-lg">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 text-center">
          Create your account
        </h2>
        <p className="mt-2 text-sm text-gray-600 text-center">
          Already have an account?{' '}
          <Link to="/" className="text-primary-600 hover:text-primary-500 font-medium">
            Sign in here
          </Link>
        </p>
      </div>

      {errors.general && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleRegisterSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
              First Name *
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              className={`input mt-1 ${errors.firstName ? 'border-red-300' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
            />
            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
              Last Name *
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              className={`input mt-1 ${errors.lastName ? 'border-red-300' : ''}`}
              value={formData.lastName}
              onChange={handleChange}
            />
            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className={`input mt-1 ${errors.email ? 'border-red-300' : ''}`}
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone Number *
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            className={`input mt-1 ${errors.phone ? 'border-red-300' : ''}`}
            value={formData.phone}
            onChange={handleChange}
            placeholder="Enter 10-digit mobile number"
          />
          {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          <p className="mt-1 text-xs text-gray-500">
            OTP will be sent to this WhatsApp number for verification
          </p>
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700">
            I am a *
          </label>
          <select
            id="role"
            name="role"
            required
            className={`input mt-1 ${errors.role ? 'border-red-300' : ''}`}
            value={formData.role}
            onChange={handleChange}
          >
            <option value="student">Student</option>
            {instructorRegistrationEnabled ? (
              <option value="instructor">Instructor</option>
            ) : (
              <option value="instructor" disabled>
                Instructor (Registration temporarily disabled)
              </option>
            )}
          </select>
          {!instructorRegistrationEnabled && (
            <p className="mt-1 text-sm text-gray-500">
              Instructor registration is currently disabled by administrators.
            </p>
          )}
        </div>

        {/* Instructor Profile Fields */}
        {showInstructorFields && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900">Instructor Information</h3>

            <div>
              <label htmlFor="qualification" className="block text-sm font-medium text-gray-700">
                Highest Qualification *
              </label>
              <select
                id="qualification"
                name="instructorProfile.qualification"
                required={showInstructorFields}
                className={`input mt-1 ${errors.qualification ? 'border-red-300' : ''}`}
                value={formData.instructorProfile.qualification}
                onChange={handleChange}
              >
                <option value="">Select Qualification</option>
                <option value="bachelors">Bachelor's Degree</option>
                <option value="masters">Master's Degree</option>
                <option value="phd">PhD</option>
                <option value="diploma">Diploma</option>
                <option value="certificate">Professional Certificate</option>
              </select>
              {errors.qualification && <p className="mt-1 text-sm text-red-600">{errors.qualification}</p>}
            </div>

            <div>
              <label htmlFor="experience" className="block text-sm font-medium text-gray-700">
                Years of Teaching Experience
              </label>
              <input
                id="experience"
                name="instructorProfile.experience"
                type="number"
                min="0"
                className="input mt-1"
                value={formData.instructorProfile.experience}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700">
                Bio / Summary
              </label>
              <textarea
                id="bio"
                name="instructorProfile.bio"
                rows={3}
                className="input mt-1"
                placeholder="Brief description of your teaching background and expertise..."
                value={formData.instructorProfile.bio}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="linkedIn" className="block text-sm font-medium text-gray-700">
                  LinkedIn Profile (optional)
                </label>
                <input
                  id="linkedIn"
                  name="instructorProfile.linkedIn"
                  type="url"
                  className="input mt-1"
                  placeholder="https://linkedin.com/in/yourprofile"
                  value={formData.instructorProfile.linkedIn}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700">
                  Portfolio Website (optional)
                </label>
                <input
                  id="portfolio"
                  name="instructorProfile.portfolio"
                  type="url"
                  className="input mt-1"
                  placeholder="https://yourwebsite.com"
                  value={formData.instructorProfile.portfolio}
                  onChange={handleChange}
                />
              </div>
            </div>

            {showInstructorFields && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Next Step:</strong> After registration, you'll need to upload documents
                  (degree certificates, ID proof, etc.) for verification before your account is approved.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Referral Code Field */}
        <div>
          <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700">
            Referral Code (Optional)
          </label>
          <input
            id="referralCode"
            name="referralCode"
            type="text"
            className={`input mt-1 ${referralValid === false ? 'border-red-300' : referralValid === true ? 'border-green-300' : ''}`}
            value={formData.referralCode}
            onChange={(e) => {
              const value = e.target.value.toUpperCase();
              setFormData({ ...formData, referralCode: value });
              handleChange(e);
            }}
            placeholder="Enter referral code to get benefits"
          />
          {referralValid === true && referrerInfo && (
            <p className="mt-1 text-sm text-green-600">
              ✓ Valid referral code from {referrerInfo.firstName} {referrerInfo.lastName}
            </p>
          )}
          {referralValid === false && (
            <p className="mt-1 text-sm text-red-600">
              ✗ Invalid referral code
            </p>
          )}
          {formData.referralCode && referralValid === null && (
            <p className="mt-1 text-sm text-yellow-600">
              Checking referral code...
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password *
          </label>
          <div className="mt-1 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              className={`input pr-10 ${errors.password ? 'border-red-300' : ''}`}
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeSlashIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Confirm Password *
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            className={`input mt-1 ${errors.confirmPassword ? 'border-red-300' : ''}`}
            value={formData.confirmPassword}
            onChange={handleChange}
          />
          {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn btn-primary py-3 text-sm font-medium disabled:opacity-50"
          >
            {isLoading ? 'Sending OTP...' : 'Send OTP & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Register;
